import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ContactForm from "../components/ContactForm";
import ReviewsSection from "../components/ReviewsSection";
import MemorialBanner from "../components/MemorialBanner";
import { IMAGES, INSTRUCTORS, GALLERY } from "../data/mock";
import { useLightbox } from "../components/Lightbox";
import { useLang } from "../context/LanguageContext";

const Home = () => {
  const { t } = useLang();
  const previewImages = GALLERY.slice(0, 4);
  const { openAt, lightbox } = useLightbox(GALLERY);

  return (
    <main>
      <MemorialBanner />

      <ContactForm titleKey="form.hero_title" variant="hero" formType="Begin Journey" />

      <section className="bg-white dark:bg-black py-16 md:py-20 transition-colors">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            <div className="rounded-lg overflow-hidden border-4 border-[#800000] shadow-xl">
              <img src={IMAGES.main} alt="Yoshitaka Karate Dojo" className="w-full h-full object-cover min-h-[300px]" />
            </div>
            <div className="bg-[#14532d] text-white rounded-lg p-8 md:p-12 flex flex-col justify-center text-center shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#800000]/30 rounded-full blur-3xl" />
              <h2 className="relative text-4xl md:text-5xl font-bold mb-4">{t("welcome.title")}</h2>
              <h3 className="relative text-xl md:text-2xl font-medium mb-8 text-white/95">{t("welcome.subtitle")}</h3>
              <div className="relative flex justify-center">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#800000] text-white hover:bg-[#5a0000] px-8 py-3 rounded-md font-bold shadow-md hover:shadow-lg transition-all group">
                  {t("welcome.cta")}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#800000] py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[#14532d] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="text-white text-center md:text-left order-2 md:order-1">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">{t("about_home.title")}</h2>
              <p className="text-white/95 text-base md:text-lg leading-relaxed mb-6">{t("about_home.body")}</p>
              <Link to="/about" className="inline-flex items-center gap-2 bg-[#14532d] text-white hover:bg-[#0f3d20] px-6 py-3 rounded-md font-bold shadow-md hover:shadow-lg transition-all group">
                {t("about_home.cta")}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="order-1 md:order-2 rounded-lg overflow-hidden shadow-2xl border-4 border-white/30">
              <img src={IMAGES.about} alt="Karate practice" className="w-full h-full object-cover min-h-[320px]" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white dark:bg-black py-16 md:py-20 transition-colors">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#14532d] rounded-lg p-8 md:p-12 text-center text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#800000]" />
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#800000]" />
            <p className="uppercase tracking-[0.3em] text-sm md:text-base text-emerald-200 mb-4">{t("ourclass.eyebrow")}</p>
            <div className="my-6 rounded-md overflow-hidden border-4 border-white/90 shadow-lg max-w-2xl mx-auto">
              <img src={IMAGES.classImg} alt="Karate class in session" className="w-full h-auto" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mt-6">{t("ourclass.heading")}</h3>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-zinc-950 py-16 md:py-20 transition-colors">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#800000] dark:text-[#b85a6f] uppercase tracking-[0.3em] text-xs md:text-sm font-semibold mb-2">{t("gallery_home.eyebrow")}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">{t("gallery_home.title")}</h2>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="h-1 w-12 bg-[#800000] rounded-full" />
              <div className="h-1 w-6 bg-[#14532d] rounded-full" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
            {previewImages.map((g, i) => {
              const caption = g.captionKey ? t(`gallery_captions.${g.captionKey}`) : g.caption || "";
              return (
                <button key={g.id} onClick={() => openAt(i)} className="group relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300" aria-label={`Open ${caption}`}>
                  <img src={g.src} alt={caption} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#800000]/90 via-[#800000]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>

          <div className="flex justify-center">
            <Link to="/gallery" className="inline-flex items-center gap-2 bg-[#800000] text-white hover:bg-[#5a0000] px-6 py-3 rounded-md font-semibold shadow-md hover:shadow-lg transition-all group">
              {t("gallery_home.view_full")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 dark:bg-black py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#b85a6f] uppercase tracking-[0.3em] text-xs md:text-sm font-semibold mb-2">{t("instructors.eyebrow")}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">{t("instructors.title")}</h2>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="h-1 w-12 bg-[#800000] rounded-full" />
              <div className="h-1 w-6 bg-emerald-500 rounded-full" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {INSTRUCTORS.map((ins) => (
              <div key={ins.id} className="bg-[#14532d] text-white rounded-lg p-8 text-center shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#800000]" />
                <h4 className="text-xl font-bold mb-4 min-h-[56px] flex items-center justify-center">{ins.name}</h4>
                <div className="text-sm text-white/90 leading-relaxed mb-6">
                  <p className="font-semibold mb-1 text-emerald-200">{t("instructors.location_label")}</p>
                  <p>{ins.location}</p>
                  <p className="mt-2">{t("instructors.phone_label")} {ins.phone}</p>
                </div>
                <div className="flex justify-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#800000] shadow-lg">
                    <img src={ins.image} alt={ins.name} className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReviewsSection />

      {lightbox}
    </main>
  );
};

export default Home;
